import React, { useState } from 'react';
import Drawer from '../../../Drawer/Drawer';
import { Heading } from '@legacy-vfuk/core-heading';
import { Icon } from '@legacy-vfuk/core-icon';
import { css } from 'styled-components';
import { Spacing } from '@legacy-vfuk/core-spacing';
import { Divider } from '@legacy-vfuk/core-divider';
import FilterPill from './FilterPill';
import {
  FilterContentType,
  FilterList,
  FilterTime,
  TagTopic,
} from './types/SectionFilterableArticles.types';
import {
  AppliedFiltersContainer,
  ButtonGroupContainer,
  ClearFiltersLink,
  DrawerFilterContainer,
  FilterPillContainer,
  HeaderContainer,
} from './styles/DrawerFilter.styles';
import { Button } from '@legacy-vfuk/core-button';
import DrawerCheckboxList from './DrawerCheckboxList';

type DrawerFilterProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  tagTopics: TagTopic[];
  filterTime: FilterTime[];
  filterContentype: FilterContentType[];
  filterList: FilterList;
  setFilterList: React.Dispatch<React.SetStateAction<FilterList>>;
  setSeoData: React.Dispatch<React.SetStateAction<any>>;
  uiTextLinkClear: string;
  uiTextFilterByCategories: string;
  uiTextFilterApplied: string;
  uiTextTimeFilterLabel: string;
  uiTextContentTypeLabel: string;
  uiTextCategoriesSubCategories: string;
  uiTextCancelFilterButton: string;
  uiTextApplyFilterButton: string;
};

const DrawerFilter = ({
  isOpen,
  setIsOpen,
  filterTime,
  filterContentype: filterContenTypes,
  filterList,
  tagTopics,
  setFilterList,
  setSeoData,
  uiTextLinkClear,
  uiTextFilterByCategories,
  uiTextFilterApplied,
  uiTextTimeFilterLabel,
  uiTextContentTypeLabel,
  uiTextCategoriesSubCategories,
  uiTextCancelFilterButton,
  uiTextApplyFilterButton,
}: DrawerFilterProps) => {
  const [tempFilterList, setTempFilterList] = useState(filterList);

  const onClearAllFiltersClick = () => {
    setTempFilterList(prevFilterList => ({
      ...prevFilterList,
      topic: '',
      topicValue: '',
      selectedTopics: [],
      timeRange: [],
      contentType: [],
    }));
  };

  const hasFiltersSelected = () => {
    return (
      tempFilterList.selectedTopics.length > 0 ||
      tempFilterList.timeRange.length > 0 ||
      tempFilterList.contentType.length > 0
    );
  };

  const saveTempFilterList = () => {
    const newTempFilterList = {
      ...tempFilterList,
      topic: tempFilterList.selectedTopics
        .map(topic => topic.displayName)
        .join(','),
      topicValue: tempFilterList.selectedTopics
        .map(topic => topic.slugName)
        .join(','),
      // This means that every time the filter is applied, the paginator must return to the first page of results
      page: 0,
    };
    setFilterList(newTempFilterList);
  };

  const removeTopicFromFilterList = (topicTarget: TagTopic) => {
    setTempFilterList(prevFilterList => {
      const newTopic = prevFilterList.topic
        .split(',')
        .filter(topic => topic !== topicTarget.displayName)
        .join(',');

      const newTopicValue = prevFilterList.topicValue
        .split(',')
        .filter(topic => topic !== topicTarget.slugName)
        .join(',');

      const newSelectedTopics = prevFilterList.selectedTopics.filter(
        topic => topic.slugName !== topicTarget.slugName
      );

      return {
        ...prevFilterList,
        topic: newTopic,
        topicValue: newTopicValue,
        selectedTopics: newSelectedTopics,
      };
    });
  };

  const removeTimeFilterFromFilterList = (timeFilterTarget: FilterTime) => {
    setTempFilterList(prevFilterList => {
      const newTimeRange = prevFilterList.timeRange.filter(
        time => time.slugName !== timeFilterTarget.slugName
      );
      return {
        ...prevFilterList,
        timeRange: newTimeRange,
      };
    });
  };

  const removeContentTypeFromFilterList = (
    timeFilterTarget: FilterContentType
  ) => {
    setTempFilterList(prevFilterList => {
      const newTimeRange = prevFilterList.contentType.filter(
        contentType => contentType.slugName !== timeFilterTarget.slugName
      );
      return {
        ...prevFilterList,
        contentType: newTimeRange,
      };
    });
  };

  const updateContentTypeFilter = (slugName: string) => {
    setTempFilterList(prevFilterList => {
      const selectedFilterContentType = filterContenTypes.find(
        time => time.slugName === slugName
      );

      if (!selectedFilterContentType) {
        return { ...prevFilterList };
      }

      const isValueInTimeRange = prevFilterList.contentType.some(
        selectedContentType => selectedContentType.slugName === slugName
      );

      return {
        ...prevFilterList,
        contentType: isValueInTimeRange
          ? prevFilterList.contentType.filter(
              contentType => contentType.slugName !== slugName
            )
          : [...prevFilterList.contentType, selectedFilterContentType],
      };
    });

    setSeoData({
      event_type: 'content_type',
      filter_query: slugName,
    });
  };

  const updateTimeRangeFilter = (slugName: string) => {
    setTempFilterList(prevFilterList => {
      const selectedTimeRange = filterTime.find(
        time => time.slugName === slugName
      ) as FilterTime;

      const isValueInTimeRange = prevFilterList.timeRange.some(
        selectedTime => selectedTime.slugName === slugName
      );

      return {
        ...prevFilterList,
        timeRange: isValueInTimeRange
          ? prevFilterList.timeRange.filter(time => time.slugName !== slugName)
          : [...prevFilterList.timeRange, selectedTimeRange],
      };
    });

    setSeoData({
      event_type: 'time',
      filter_query: slugName,
    });
  };

  const updateTagTopicFilter = (slugName: string) => {
    setTempFilterList(prevFilterList => {
      const selectedTopic = tagTopics.find(
        topic => topic.slugName === slugName
      );

      if (!selectedTopic) {
        return { ...prevFilterList };
      }

      const isValueInTopicValue = prevFilterList.selectedTopics.find(
        tagTopic => tagTopic.slugName === slugName
      );

      return {
        ...prevFilterList,
        selectedTopics: isValueInTopicValue
          ? prevFilterList.selectedTopics.filter(
              selectedTopic => selectedTopic.slugName !== slugName
            )
          : [...prevFilterList.selectedTopics, selectedTopic],
      };
    });

    setSeoData({
      event_type: 'topic',
      filter_query: slugName,
    });
  };

  const onApplyClick = () => {
    saveTempFilterList();
    setIsOpen(false);
  };

  return (
    <Drawer
      isOpen={isOpen}
      position="right"
      setIsOpen={setIsOpen}
      size={'600px'}
    >
      <DrawerFilterContainer>
        <Spacing spacingLevel={{ bottom: 5 }}>
          <HeaderContainer>
            <Heading size={2} weight={3} noMargin>
              {uiTextFilterByCategories}
            </Heading>

            <div
              css={css`
                cursor: pointer;
              `}
              onClick={() => setIsOpen(false)}
            >
              <Icon name="close" size={5} />
            </div>
          </HeaderContainer>
        </Spacing>

        <Divider appearance="secondary" />

        {hasFiltersSelected() && (
          <Spacing spacingLevel={{ bottom: 5 }}>
            <Spacing spacingLevel={{ bottom: 2 }}>
              <AppliedFiltersContainer>
                <Heading size={1} weight={3} noMargin>
                  {uiTextFilterApplied}
                </Heading>

                <ClearFiltersLink onClick={onClearAllFiltersClick}>
                  {uiTextLinkClear}
                </ClearFiltersLink>
              </AppliedFiltersContainer>
            </Spacing>

            <FilterPillContainer>
              {tempFilterList.contentType.map(contentType => (
                <FilterPill
                  key={contentType.name}
                  item={contentType}
                  removeItemFromFilterList={removeContentTypeFromFilterList}
                />
              ))}
              {tempFilterList.timeRange.map(time => (
                <FilterPill
                  item={time}
                  removeItemFromFilterList={removeTimeFilterFromFilterList}
                  key={time.name}
                />
              ))}
              {tempFilterList.selectedTopics.map(topic => (
                <FilterPill
                  item={topic}
                  removeItemFromFilterList={removeTopicFromFilterList}
                  key={topic.name}
                />
              ))}
            </FilterPillContainer>
          </Spacing>
        )}

        {filterContenTypes && (
          <Spacing spacingLevel={{ bottom: 5 }}>
            <DrawerCheckboxList
              label={uiTextContentTypeLabel}
              tempFilterList={tempFilterList.contentType}
              filters={filterContenTypes}
              updateFilterList={updateContentTypeFilter}
            />
          </Spacing>
        )}

        {filterTime && filterTime.length > 0 && (
          <Spacing spacingLevel={{ bottom: 5 }}>
            <DrawerCheckboxList
              label={uiTextTimeFilterLabel}
              tempFilterList={tempFilterList.timeRange}
              filters={filterTime}
              updateFilterList={updateTimeRangeFilter}
            />
          </Spacing>
        )}

        <Divider appearance="secondary" />

        <Spacing spacingLevel={{ bottom: 5 }}>
          <Heading size={2} weight={3}>
            {uiTextCategoriesSubCategories}
          </Heading>
        </Spacing>

        <Spacing spacingLevel={{ bottom: 5 }} key="topicsCheckBoxList">
          <DrawerCheckboxList
            label={uiTextCategoriesSubCategories}
            tempFilterList={tempFilterList.selectedTopics}
            updateFilterList={updateTagTopicFilter}
            filters={tagTopics}
          />
        </Spacing>

        <Divider appearance="secondary" />

        <ButtonGroupContainer>
          <Button
            text={uiTextCancelFilterButton}
            appearance="alt1"
            onClick={() => setIsOpen(false)}
          />
          <Button
            text={uiTextApplyFilterButton}
            appearance="primary"
            onClick={onApplyClick}
          />
        </ButtonGroupContainer>
      </DrawerFilterContainer>
    </Drawer>
  );
};

export default DrawerFilter;
